import {Box, Typography, BoxProps, Fade} from '@mui/material'
import React, {FC, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

const MIN_SAVING_VISIBLE_MS = 500
const SAVED_VISIBLE_MS = 2000

interface SavingIndicatorProps extends BoxProps {
  isSaving: boolean
}
export const SavingIndicator: FC<SavingIndicatorProps> = ({isSaving, ...boxProps}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [showSaving, setShowSaving] = useState(false)
  const [shouldShowSaved, setShouldShowSaved] = useState(false)
  const savingStartedAt = useRef<number | null>(null)

  useEffect(() => {
    let hideSavingTimeoutId: ReturnType<typeof setTimeout>
    let hideSavedTimeoutId: ReturnType<typeof setTimeout>

    if (isSaving) {
      // Latch the "Saving" indicator on and remember when it started, so a very
      // fast save (sub-frame) still keeps the indicator visible long enough to be
      // perceivable (and reliably observable in tests).
      savingStartedAt.current = performance.now()
      setShowSaving(true)
      setShouldShowSaved(false)
    } else if (savingStartedAt.current !== null) {
      const elapsed = performance.now() - savingStartedAt.current
      const remaining = Math.max(0, MIN_SAVING_VISIBLE_MS - elapsed)
      savingStartedAt.current = null
      hideSavingTimeoutId = setTimeout(() => {
        setShowSaving(false)
        setShouldShowSaved(true)
        hideSavedTimeoutId = setTimeout(() => {
          setShouldShowSaved(false)
        }, SAVED_VISIBLE_MS)
      }, remaining)
    }

    return () => {
      clearTimeout(hideSavingTimeoutId)
      clearTimeout(hideSavedTimeoutId)
    }
  }, [isSaving])

  return (
    <Box {...boxProps}>
      {showSaving ? <Typography variant="body1">{`... ${t('common.saving')}`}</Typography> : null}
      {shouldShowSaved ? (
        <Fade in timeout={{exit: 1000}}>
          <Typography variant="body1">{t('common.saved')}</Typography>
        </Fade>
      ) : null}
    </Box>
  )
}
